// ============================================
// FILE: src/pages/HomePage.jsx
// ============================================
import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight,  ChevronLeft } from 'lucide-react';

import PropertyCard from '../components/PropertyCard';
import ServiceCard from '../components/ServiceCard';


import InquiryForm from '../components/InquiryForm';
import { properties } from '../data/properties';
import HeroSection from '../components/HeroSection';
import AuthorizedPartnerPage from '../components/AuthorizedPartnerPage';
import Reviews from '../components/Reviews';
 

console.log("ServiceCard =", ServiceCard);

const HomePage = () => {
  const featuredProperties = properties.filter(p => p.featured);
 

  // Carousel state
  const scrollContainerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);

  // Calculate visible cards based on screen width
  useEffect(() => {
    const updateVisibleCards = () => {
      if (window.innerWidth >= 1280) setVisibleCards(3);
      else if (window.innerWidth >= 1024) setVisibleCards(2);
      else setVisibleCards(1);
    };
    
    updateVisibleCards();
    window.addEventListener('resize', updateVisibleCards);
    return () => window.removeEventListener('resize', updateVisibleCards);
  }, []);

  // Check scroll position
  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
      
      // Calculate current index based on scroll position
      const cardWidth = 380 + 32; // card width + gap
      const newIndex = Math.round(container.scrollLeft / cardWidth);
      setCurrentIndex(newIndex);
    }
  };

  // Auto-scroll functionality
  useEffect(() => {
    if (isHovering || featuredProperties.length <= visibleCards) return;
    
    const interval = setInterval(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const cardWidth = 380 + 32; // card width + gap
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        const currentScroll = container.scrollLeft;
        
        if (currentScroll >= maxScrollLeft - 10) {
          // Reset to start seamlessly
          container.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
          setCurrentIndex(0);
        } else {
          const nextScroll = currentScroll + cardWidth;
          container.scrollTo({
            left: nextScroll,
            behavior: 'smooth'
          });
          setCurrentIndex(Math.round(nextScroll / cardWidth));
        }
      }
    }, 4000); // Auto move every 4 seconds

    return () => clearInterval(interval);
  }, [isHovering, featuredProperties.length, visibleCards]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = 380 + 32; // card width + gap
      
      if (direction === 'left') {
        container.scrollBy({
          left: -cardWidth,
          behavior: 'smooth'
        });
      } else {
        container.scrollBy({
          left: cardWidth,
          behavior: 'smooth'
        });
      }
      
      setTimeout(checkScrollButtons, 300);
    }
  };

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons();
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, []);

  // Calculate total pages for dots
  const totalPages = Math.ceil(featuredProperties.length / visibleCards);

  return (
    <div>
      <HeroSection/>
      
      {/* Services Section */}
      <div className="w-full bg-gradient-to-br from-gray-100 via-white to-gray-50 py-16">
        {/* Section header */}
        <div className="text-center mb-10">
          <div className="inline-block mb-4">
            <span 
              className="inline-flex items-center px-4 py-1 rounded-full text-sm font-semibold bg-blue-50 text-blue-600 border border-blue-100"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="w-2 h-2 bg-blue-600 rounded-full mr-2 animate-pulse"></span>
              What We Offer
            </span>
          </div>
          
          <h2 
            className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Our Premium Services
          </h2>
          
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-3 rounded-full"></div>
          
          <p 
            className="text-gray-600 max-w-2xl mx-auto text-lg"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Complete real estate solutions under one roof — trusted by thousands of happy customers
          </p>
        </div>

        {/* Services grid - 3 cards properly centered */}
        

<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
  <Link to="/rent" className="block transition-transform hover:scale-105">
    <ServiceCard 
      icon={
        <div className="bg-white rounded-full p-1">
          <img src="/images/Rent_icon.webp" alt="icon" className="w-14 h-14" />
        </div>
      } 
      title="Property for Rent" 
      description="Wide range of rental properties across Mumbai — from budget to luxury"
      gradient="bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700"
    />
  </Link>
  
  <Link to="/sale" className="block transition-transform hover:scale-105">
    <ServiceCard 
      icon={
        <div className="bg-white rounded-full p-1">
          <img src="/images/sale_icon.png" alt="icon" className="w-12 h-12" />
        </div>
      } 
      title="Property for Sale" 
      description="Premium residential & commercial properties with best market prices"
      gradient="bg-gradient-to-br from-amber-500 via-orange-500 to-red-600"
    />
  </Link>
  
  <Link to="/agreements" className="block transition-transform hover:scale-105">
    <ServiceCard 
      icon={
        <div className="bg-white rounded-full p-1">
          <img src="/images/Agrement_icon.png" alt="icon" className="w-12 h-12" />
        </div>
      } 
      title="Legal Agreements" 
      description="Expert drafting of rental & sale agreements with full compliance"
      gradient="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-700"
    />
  </Link>

  <Link to="/interior" className="block transition-transform hover:scale-105">
  <ServiceCard 
    icon={
      <div className="bg-white rounded-full p-1">
        <img src="/images/interior_icon.png" alt="icon" className="w-12 h-12" />
      </div>
    } 
    title="Interior Design" 
    description="Professional interior design solutions tailored to your style and space"
    gradient="bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-700"
  />
</Link>


<Link to="/home-loan" className="block transition-transform hover:scale-105">
  <ServiceCard 
    icon={
      <div className="bg-white rounded-full p-1">
        <img src="/images/homeloan_icon.jpg" alt="icon" className="w-12 h-12" />
      </div>
    } 
    title="Home Loan" 
    description="Easy home loan approvals with competitive interest rates & flexible tenures"
    gradient="bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-600"
  />
</Link>
</div>





      </div>

      {/* Featured Properties with Carousel */}
      <div className="container mx-auto px-1 py-2">
        <div className="relative text-center mb-12 py-3">
  {/* Decorative background accent */}
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
    <div className="w-64 h-64 bg-gradient-to-br from-blue-100/40 to-purple-100/30 rounded-full blur-3xl" />
  </div>

  {/* Eyebrow label */}
  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 text-blue-600 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4 shadow-sm">
    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
    Premium Listings
  </div>

  {/* Main Heading */}
  <h2 className="relative text-4xl md:text-5xl font-extrabold tracking-tight mb-3">
    <span className="bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-700 bg-clip-text text-transparent">
      Featured Properties
    </span>
  </h2>

  {/* Decorative divider */}
  <div className="flex items-center justify-center gap-3 mb-3">
    <div className="h-px w-12 bg-gradient-to-r from-transparent to-blue-400" />
    <div className="w-2 h-2 rounded-full bg-blue-500 shadow-md shadow-blue-300" />
    <div className="h-px w-12 bg-gradient-to-l from-transparent to-blue-400" />
  </div>

  {/* Subtitle */}
  <p className="text-gray-500 text-base md:text-lg mb-3">
    Handpicked premium listings curated just for you
  </p>

  {/* View All CTA */}
  <Link
    to="/sale"
    className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 px-5 py-2 rounded-full shadow-md shadow-blue-200 transition-all duration-200 hover:shadow-lg hover:shadow-blue-300 hover:-translate-y-0.5"
  >
    View All Properties <ChevronRight size={16} />
  </Link>
</div>

        {/* Carousel Section */}
        <div 
          className="relative"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* Left Navigation Button */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 text-blue-600 hover:text-blue-700 border border-gray-200"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto scroll-smooth gap-8 py-2 px-1 hide-scrollbar"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              scrollBehavior: 'smooth'
            }}
          >
            {featuredProperties.map((property) => (
              <div key={property.id} className="flex-shrink-0 w-[380px]">
                <PropertyCard property={property} />
              </div>
            ))}
          </div>

          {/* Right Navigation Button */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 text-blue-600 hover:text-blue-700 border border-gray-200"
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>

        {/* Dots Indicator */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-3">
            {Array.from({ length: totalPages }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (scrollContainerRef.current) {
                    const cardWidth = 380 + 32; // card width + gap
                    const scrollAmount = idx * cardWidth * visibleCards;
                    scrollContainerRef.current.scrollTo({
                      left: scrollAmount,
                      behavior: 'smooth'
                    });
                    setCurrentIndex(idx * visibleCards);
                  }
                }}
                className={`transition-all duration-300 rounded-full ${
                  Math.floor(currentIndex / visibleCards) === idx 
                    ? 'w-6 h-2 bg-blue-600' 
                    : 'w-2 h-2 bg-gray-300 hover:bg-blue-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dealer Section */}
      <div className="container mx-auto ">
        <AuthorizedPartnerPage />
      </div>
 <div className="container mx-auto ">
        <Reviews />
      </div>
    

      {/* Inquiry Form */}
      <div className="container mx-auto ">
        <div className="max-w-3xl mx-auto">
          <InquiryForm />
        </div>
      </div>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default HomePage;